"use client";

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
  useRef,
} from "react";
import { useAccountSelection } from "./account-selection-context";
import { apiClient } from "@/lib/api-client";
import { useAccountBalances } from "@/contexts/account-balances-context";
import {
  positionMetricsService,
  type PositionAttributeEntry,
} from "@/services/position-metrics-service";
import { positionStrategyDetector } from "@/services/position-strategy-detector";
import type { DetectedStrategy } from "@/services/position-strategy-detector";
import { extractPositionsFromResponse, isPlainObject } from "@/hooks/types";
import {
  getPositionIntentFlags,
  isPositionIntent,
  type PositionIntent,
} from "@/lib/position-intents";
import type {
  EnhancedPosition,
  PortfolioMetrics,
  Position,
} from "@/types/position.types";

interface GroupedPosition {
  symbol: string;
  positions: Position[];
  totalQuantity: number;
  totalMarketValue: number;
  /**
   * Always null: this grouping works only from broker position fields, which
   * carry no prior close or live quote, so a real day change cannot be
   * derived here. (It was previously faked as
   * average-daily-market-close-price × qty × mult — not a change at all.)
   * Real day change comes from position-snapshot-service
   * (live quote − prior close) via the positions table.
   */
  totalDayChange: number | null;
  totalCost: number;
  totalPL: number;
  totalPositionDelta: number;
  totalNotional: number;
}

// Health of the single broker margin-requirements fetch this context owns.
// "ready" means every selected account returned fresh broker data with no
// warning; "degraded" means at least one account failed or came back with the
// route's cached-data warning — consumers must NOT trust broker BPR values in
// that state and must surface the warning visibly.
export type BrokerMarginState = "idle" | "loading" | "ready" | "degraded";

interface PositionsContextType {
  positions: Position[];
  groupedPositions: GroupedPosition[];
  enhancedPositions: EnhancedPosition[];
  detectedStrategies: DetectedStrategy[];
  portfolioMetrics: PortfolioMetrics | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  // Single owner of the /margin-requirements GET per load: consumers (the
  // positions table's BPR column) read this instead of fetching again.
  brokerMargins: Map<string, BrokerMarginRequirement>;
  brokerMarginState: BrokerMarginState;
  brokerMarginWarning: string | null;
}

export interface BrokerMarginRequirement {
  accountNumber: string;
  positionKey: string;
  allocatedBuyingPower: number | null;
  groupMarginRequirement: number | null;
  groupKey: string;
  syncedAt: string;
}

interface BrokerMarginResponse {
  positions?: BrokerMarginRequirement[];
  // The route returns 200 with this set when the broker refresh failed and it
  // served cached rows (or nothing). It must never be silently discarded.
  warning?: string;
}

const PositionsContext = createContext<PositionsContextType | undefined>(
  undefined,
);

export function usePositions() {
  const context = useContext(PositionsContext);
  if (!context) {
    throw new Error("usePositions must be used within a PositionsProvider");
  }
  return context;
}

interface PositionsProviderProps {
  children: ReactNode;
}

function getErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  if (error && typeof error === "object" && "message" in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === "string" && message.trim()) {
      return message;
    }
  }
  return fallback;
}

const toNumber = (value: number | string | null | undefined, fallback = 0): number => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? fallback : parsed;
  }
  return fallback;
};

const toAttributeEntries = (value: unknown): PositionAttributeEntry[] => {
  if (Array.isArray(value)) {
    return value.filter((entry): entry is PositionAttributeEntry =>
      entry !== null &&
      typeof entry === "object" &&
      typeof (entry as PositionAttributeEntry).attribute_type === "string",
    );
  }

  if (value && typeof value === "object") {
    return Object.entries(value as Record<string, unknown>).map(
      ([attributeType, attributeValue]) => ({
        attribute_type: attributeType,
        attribute_value: attributeValue,
      }),
    );
  }

  return [];
};

const getAttributeValue = (
  entries: PositionAttributeEntry[],
  attributeType: string,
): unknown => entries.find((entry) => entry.attribute_type === attributeType)
  ?.attribute_value;

const getIntentFromAttributes = (
  entries: PositionAttributeEntry[],
): PositionIntent | null => {
  const value = getAttributeValue(entries, "position_intent");
  if (isPositionIntent(value)) {
    return value;
  }
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return isPositionIntent(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }
  return null;
};

const compactSymbol = (symbol: string): string =>
  symbol.replace(/\s+/g, "").toUpperCase();

const marginLookupKeys = (
  accountNumber: string,
  symbol: string | null | undefined,
): string[] => {
  const rawSymbol = symbol?.trim() ?? "";
  return [
    `${accountNumber}:${rawSymbol}`,
    `${accountNumber}:${compactSymbol(rawSymbol)}`,
  ];
};

interface BrokerMarginFetchResult {
  rows: Map<string, BrokerMarginRequirement>;
  // Set when the broker data is failed or stale for this account (route
  // warning or fetch/HTTP failure). Rows may still be present (cached) but
  // must not be presented as fresh broker truth.
  warning: string | null;
}

const fetchBrokerMarginRequirements = async (
  accountNumber: string,
): Promise<BrokerMarginFetchResult> => {
  const response = await fetch(
    `/api/accounts/${encodeURIComponent(accountNumber)}/margin-requirements`,
    { credentials: "include" },
  );

  if (!response.ok) {
    throw new Error(
      `Failed to fetch broker margin requirements (HTTP ${response.status})`,
    );
  }

  const data = (await response.json()) as BrokerMarginResponse;
  const rows = new Map<string, BrokerMarginRequirement>();
  for (const row of data.positions ?? []) {
    for (const key of marginLookupKeys(accountNumber, row.positionKey)) {
      rows.set(key, row);
    }
  }
  return { rows, warning: data.warning ?? null };
};


export function PositionsProvider({ children }: PositionsProviderProps) {
  const { selectedAccounts } = useAccountSelection();
  const { ensureBalance, getBalance } = useAccountBalances();
  // getBalance gets a new identity every time any balance updates, and
  // fetchPositions itself updates balances via ensureBalance. Depending on
  // getBalance directly therefore re-triggered the fetch effect in a loop
  // (measured: /api/positions/{account} fired 5x per page load). Read it
  // through a ref so the effect only refires when the selection changes.
  const getBalanceRef = useRef(getBalance);
  useEffect(() => {
    getBalanceRef.current = getBalance;
  }, [getBalance]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [groupedPositions, setGroupedPositions] = useState<GroupedPosition[]>(
    [],
  );
  const [enhancedPositions, setEnhancedPositions] = useState<
    EnhancedPosition[]
  >([]);
  const [detectedStrategies, setDetectedStrategies] = useState<
    DetectedStrategy[]
  >([]);
  const [portfolioMetrics, setPortfolioMetrics] =
    useState<PortfolioMetrics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [brokerMargins, setBrokerMargins] = useState<
    Map<string, BrokerMarginRequirement>
  >(new Map());
  const [brokerMarginState, setBrokerMarginState] =
    useState<BrokerMarginState>("idle");
  const [brokerMarginWarning, setBrokerMarginWarning] = useState<string | null>(
    null,
  );

  const groupPositionsBySymbol = (positions: Position[]): GroupedPosition[] => {
    const grouped = new Map<string, GroupedPosition>();

    positions.forEach((position) => {
      const symbol = position["underlying-symbol"] || position.symbol;

      if (!grouped.has(symbol)) {
        grouped.set(symbol, {
          symbol,
          positions: [],
          totalQuantity: 0,
          totalMarketValue: 0,
          totalDayChange: null,
          totalCost: 0,
          totalPL: 0,
          totalPositionDelta: 0,
          totalNotional: 0,
        });
      }

      const group = grouped.get(symbol)!;
      group.positions.push(position);

      // Calculate values (matching positions-table logic)
      const quantity = toNumber(position.quantity);
      const closePrice = toNumber(position["close-price"]);
      const averageOpenPrice = toNumber(position["average-open-price"]);
      const multiplier = toNumber(position.multiplier, 1);
      const marketValue = quantity * closePrice * multiplier;
      const cost = Math.abs(quantity) * averageOpenPrice * multiplier;
      const pl = marketValue - cost;
      // No day change computed here — see the GroupedPosition.totalDayChange
      // doc comment. The broker position payload alone cannot yield one.

      // Calculate position delta and notional (matching positions-table logic)
      const instrumentType = position["instrument-type"] ?? "";
      let delta = 0;
      if (instrumentType === "Equity") {
        // For stocks, delta equals position direction
        delta = quantity > 0 ? 1.0 : -1.0;
      } else if (instrumentType === "Equity Option") {
        // For options, we don't have Greeks data here, so use conservative estimate
        // This is a limitation - actual Greeks should come from positions table
        delta = 0.5;
      }

      const positionDelta = delta * quantity * multiplier;

      // Note: This is a simplified calculation without real-time prices
      // The enhanced positions from position metrics service have accurate notional values
      const notional = 0; // Will be calculated properly in position metrics service

      group.totalQuantity += quantity;
      group.totalMarketValue += marketValue;
      group.totalCost += cost;
      group.totalPL += pl;
      group.totalPositionDelta += positionDelta;
      group.totalNotional += notional;
    });

    return Array.from(grouped.values()).sort(
      (a, b) => Math.abs(b.totalMarketValue) - Math.abs(a.totalMarketValue),
    );
  };

  const fetchPositions = useCallback(async () => {
    if (selectedAccounts.length === 0) {
      setPositions([]);
      setGroupedPositions([]);
      setEnhancedPositions([]);
      setPortfolioMetrics(null);
      setBrokerMargins(new Map());
      setBrokerMarginState("idle");
      setBrokerMarginWarning(null);
      return;
    }

    setLoading(true);
    setError(null);
    setBrokerMarginState("loading");
    setBrokerMarginWarning(null);

    try {
      const allPositions: Position[] = [];
      let totalNetLiquidatingValue = 0;
      const allAttributes = new Map<string, PositionAttributeEntry[]>();

      const accountResults = await Promise.allSettled(
        selectedAccounts.map(async (account) => {
          const accountNumber = account.account["account-number"];
          // Fetch positions first (critical)
          const positionsResponse =
            await apiClient.fetchPositions(accountNumber);

          // Fetch balance, attributes, and broker BPR in parallel.
          const [balanceResponse, attributesResponse, marginRequirements] = await Promise.all([
            ensureBalance(accountNumber, { staleMs: 60000 }).catch(() => {
              const cached = getBalanceRef.current(accountNumber);
              return cached?.balance ?? null;
            }),
            // Add timeout to attributes fetch to prevent hanging
            Promise.race([
              fetch(
                `/api/positions/attributes/simple?account=${accountNumber}&bulk=true`,
              )
                .then((res) => {
                  if (!res.ok) {
                    // Attributes API returned error
                    return { attributes: {} };
                  }
                  return res.json();
                })
                .catch(() => {
                  // Failed to fetch attributes
                  return { attributes: {} };
                }),
              // 5 second timeout
              new Promise((resolve) =>
                setTimeout(() => {
                  // Attributes fetch timed out
                  resolve({ attributes: {} });
                }, 5000),
              ),
            ]),
            fetchBrokerMarginRequirements(accountNumber).catch(
              (error): BrokerMarginFetchResult => {
                console.warn(
                  `[Positions] Failed to load broker margin requirements for ${accountNumber}:`,
                  error,
                );
                return {
                  rows: new Map<string, BrokerMarginRequirement>(),
                  warning:
                    error instanceof Error
                      ? error.message
                      : "Failed to fetch broker margin requirements",
                };
              },
            ),
          ]);

          return {
            accountNumber,
            positions: extractPositionsFromResponse(positionsResponse),
            balanceResponse,
            attributesResponse,
            marginRequirements,
          };
        }),
      );

      const mergedBrokerMargins = new Map<string, BrokerMarginRequirement>();
      const brokerMarginWarnings: string[] = [];

      accountResults.forEach((result, index) => {
        const accountNumber =
          selectedAccounts[index]?.account["account-number"] ?? "unknown";

        if (result.status === "rejected") {
          console.error(
            `Failed to fetch data for account ${accountNumber}:`,
            result.reason,
          );
          brokerMarginWarnings.push(
            `${accountNumber}: account data fetch failed`,
          );
          return;
        }

        const {
          positions: extractedPositions,
          balanceResponse,
          attributesResponse,
          marginRequirements,
        } =
          result.value;
          marginRequirements.rows.forEach((row, key) => {
            mergedBrokerMargins.set(key, row);
          });
          if (marginRequirements.warning) {
            brokerMarginWarnings.push(
              `${accountNumber}: ${marginRequirements.warning}`,
            );
          }
          // Add to total net liquidating value
          if (
            balanceResponse &&
            balanceResponse["net-liquidating-value"] !== undefined
          ) {
            totalNetLiquidatingValue += toNumber(
              balanceResponse["net-liquidating-value"],
            );
            // Account NLV logged
          } else {
            // No balance data
          }

          // Merge attributes
          if (isPlainObject(attributesResponse)) {
            const attributes = attributesResponse.attributes;
            if (isPlainObject(attributes)) {
              Object.entries(attributes).forEach(([key, value]) => {
                const entries = toAttributeEntries(value);
                if (entries.length > 0) {
                  allAttributes.set(`${accountNumber}_${key}`, entries);
                }
              });
            }
          }

          allPositions.push(
            ...extractedPositions.map((position) => {
              const entries =
                allAttributes.get(`${accountNumber}_${position.symbol}`) ?? [];
              const intent = getIntentFromAttributes(entries);
              const margin = marginLookupKeys(accountNumber, position.symbol)
                .map((key) => marginRequirements.rows.get(key))
                .find((row): row is BrokerMarginRequirement => row !== undefined);
              const marginFields = margin
                ? {
                    "broker-buying-power":
                      margin.allocatedBuyingPower ?? undefined,
                    "broker-margin-requirement":
                      margin.groupMarginRequirement ?? undefined,
                    "broker-margin-group-key": margin.groupKey,
                    "broker-margin-synced-at": margin.syncedAt,
                  }
                : {};
              if (!intent) {
                return { ...position, ...marginFields };
              }
              const flags = getPositionIntentFlags(intent);
              return {
                ...position,
                ...marginFields,
                "position-intent": intent,
                "is-long-term": intent === "long_term",
                "include-in-active-allocation": flags.includeInActiveAllocation,
                "include-in-recommendation-exposure":
                  flags.includeInRecommendationExposure,
              };
            }),
          );
      });

      // Publish the merged broker margin data + health for consumers (the
      // positions table's BPR column). Degraded means at least one account's
      // broker data failed or is stale-cached — consumers show "n/a", never a
      // silent $0 or an estimator number.
      setBrokerMargins(mergedBrokerMargins);
      if (brokerMarginWarnings.length > 0) {
        setBrokerMarginState("degraded");
        setBrokerMarginWarning(brokerMarginWarnings.join(" | "));
      } else {
        setBrokerMarginState("ready");
        setBrokerMarginWarning(null);
      }

      // Store raw positions
      setPositions(allPositions);

      // Log removed

      // Log sample raw position for debugging
      if (allPositions.length > 0) {
        // Position data logging removed

        const optionPosition = allPositions.find(
          (p) => p["instrument-type"] === "Equity Option",
        );
        if (optionPosition) {
          // Option position logging removed
        }

      }

      // Enhance positions with metrics
      let enhanced: EnhancedPosition[] = [];
      try {
        enhanced = await positionMetricsService.enhancePositions(allPositions);
      } catch (error) {
        console.error("Failed to enhance positions, using fallback:", error);
        // Import utilities for fallback calculation
        const {
          calculateDTE,
          getDTEBucket: resolveDteBucket,
        } = await import("@/utils/option-symbol-utils");
        
        // Fallback: create basic enhanced positions with DTE calculation
        enhanced = allPositions.map((position) => {
          let dte: number | null = null;
          let dteBucket: number | null = null;
          
          // Calculate DTE for options
          if (position["instrument-type"] === "Equity Option") {
            try {
              dte = calculateDTE(position.symbol);
              if (dte !== null) {
                dteBucket = resolveDteBucket(dte);
              }
            } catch (e) {
              console.warn(`Fallback DTE calculation failed for ${position.symbol}:`, e);
            }
          }
          
          return {
            ...position,
            delta:
              position["instrument-type"] === "Equity"
                ? position.quantity > 0
                  ? 1
                  : -1
                : 0,
            positionDelta: 0,
            notional: Math.abs(
              toNumber(position.quantity) *
                toNumber(position.multiplier, 100) *
                toNumber(position["average-open-price"]),
            ),
            dte,
            dteBucket,
            thetaDecay: 0,
            gammaRisk: 0,
            vegaExposure: 0,
            underlyingPrice: null,
            calculatedAt: new Date(),
            sector: "Uncategorized"
          } as EnhancedPosition;
        });
      }
      setEnhancedPositions(enhanced);

      // Detect strategies from enhanced positions
      const strategies = positionStrategyDetector.detectStrategies(enhanced);
      setDetectedStrategies(strategies);

      // Strategies detected

      // Calculate portfolio metrics with account NLV and attributes
      const metrics = await positionMetricsService.calculatePortfolioMetrics(
        enhanced,
        totalNetLiquidatingValue,
        allAttributes,
      );
      setPortfolioMetrics(metrics);

      // `Positions Context - Total NLV: ${totalNetLiquidatingValue}`);
      // Log removed

      // Group positions by underlying
      const grouped = groupPositionsBySymbol(enhanced);
      // "Positions Context - Grouped positions:", grouped);
      // "Positions Context - Total positions:", allPositions.length);

      // Log metrics for debugging
      // "Positions Context - Portfolio metrics:", metrics);
      // DTE distribution logging removed

      setGroupedPositions(grouped);
    } catch (err) {
      setError(getErrorMessage(err, "Failed to fetch positions"));
      setBrokerMarginState("degraded");
      setBrokerMarginWarning(
        getErrorMessage(err, "Failed to fetch positions"),
      );
      console.error("Error fetching positions:", err);
    } finally {
      setLoading(false);
    }
  }, [selectedAccounts, ensureBalance]);

  // The account-selection context restores the saved selection from
  // localStorage shortly after mount, producing a new selectedAccounts array
  // with the same accounts. Key the fetch on the account numbers so an
  // identical selection doesn't refetch; refresh() still forces a fetch.
  const lastFetchedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const key = selectedAccounts
      .map((account) => account.account["account-number"])
      .sort()
      .join(",");
    if (lastFetchedKeyRef.current === key) return;
    lastFetchedKeyRef.current = key;
    fetchPositions();
  }, [selectedAccounts, fetchPositions]);

  const value: PositionsContextType = {
    positions,
    groupedPositions,
    enhancedPositions,
    detectedStrategies,
    portfolioMetrics,
    loading,
    error,
    refresh: fetchPositions,
    brokerMargins,
    brokerMarginState,
    brokerMarginWarning,
  };

  return (
    <PositionsContext.Provider value={value}>
      {children}
    </PositionsContext.Provider>
  );
}
